import SharedDialog from '../../../../shared/shared-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, updateDocName } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { Doc } from '../../../../../types';
import { useState } from 'react';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import TextField from '@mui/material/TextField';

interface DriveRenameDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  clickedDoc: Doc;
  selectedFolder: string;
  driveId: string;
  changeLogRoles: string[];
  isAllUsers?: boolean;
}

export default function DriveRenameDialog({
  open,
  handleClose,
  userEmail,
  selectedFolder,
  clickedDoc,
  driveId,
  changeLogRoles,
  isAllUsers,
}: DriveRenameDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [newName, setNewName] = useState(clickedDoc.title);

  const isFolder = clickedDoc.mimeType.includes('folder');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      updateDocName(userEmail, clickedDoc.id, newName, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: `${isFolder ? 'Folder' : 'Document'} renamed successfully!`,
      });

      if (isFolder) {
        queryClient.invalidateQueries({ queryKey: [UserKeys.DriveFolders] });
      } else {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveDocuments, userEmail, selectedFolder],
        });
      }

      if (selectedFolder !== `${driveId}.starred` && clickedDoc.starred) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveDocuments, userEmail, `${driveId}.starred`],
        });
      }
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: `There was an issue renaming the ${
          isFolder ? 'folder' : 'document'
        }. Please refresh and try again.`,
      });
    },
    onSettled: () => {
      if (isAllUsers) {
        createEvent(analytics.drive.drive_explorer.rename_drive, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.driveExplorer,
        });
      } else {
        createEvent(analytics.user_management.user_details.drive.rename_drive, {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.drive,
        });
      }
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanRenameDocs.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Rename ${isFolder ? 'Folder' : 'Document'}`}
      actingAs={userEmail}
      dialogContent={
        <>
          <TextField
            fullWidth
            autoFocus
            value={newName}
            label={`${isFolder ? 'Folder' : 'Document'} Name`}
            onChange={(e) => setNewName(e.target.value)}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleSubmit,
        loading: isPending,
        disabled: clickedDoc.title === newName,
      }}
    />
  );
}
